import React, { useState, useEffect } from 'react';
import { crmApi } from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { ScrollArea } from '../../components/ui/scroll-area';
import { toast } from 'sonner';
import { FileText, Plus, Edit, Trash2, Loader2, Mail, Globe, Eye, Code } from 'lucide-react';

const CATEGORIES = ['onboarding', 'sales', 'newsletter', 'patient', 'product', 'conversion', 'retention'];

export default function MarketingTemplatesPage() {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [typeFilter, setTypeFilter] = useState('all');
  const [showForm, setShowForm] = useState(false);
  const [editId, setEditId] = useState(null);
  const [showPreview, setShowPreview] = useState(null);
  const [showDelete, setShowDelete] = useState(null);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ name: '', template_type: 'email', category: '', subject: '', html_content: '' });

  const load = async () => {
    setLoading(true);
    try {
      const params = typeFilter !== 'all' ? { template_type: typeFilter } : {};
      const res = await crmApi.templates.list(params);
      setTemplates(res.data.templates || []);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, [typeFilter]);

  const openCreate = () => { setForm({ name: '', template_type: 'email', category: '', subject: '', html_content: '' }); setEditId(null); setShowForm(true); };

  const openEdit = (t) => {
    setForm({ name: t.name, template_type: t.template_type, category: t.category || '', subject: t.subject || '', html_content: t.html_content || '' });
    setEditId(t.id);
    setShowForm(true);
  };

  const handleSave = async () => {
    if (!form.name) { toast.error('Template name required'); return; }
    setSaving(true);
    try {
      if (editId) {
        await crmApi.templates.update(editId, form);
        toast.success('Template updated');
      } else {
        await crmApi.templates.create(form);
        toast.success('Template created');
      }
      setShowForm(false);
      load();
    } catch (err) { toast.error('Failed to save'); }
    finally { setSaving(false); }
  };

  const handleDelete = async () => {
    setSaving(true);
    try {
      await crmApi.templates.delete(showDelete);
      toast.success('Template deleted');
      setShowDelete(null);
      load();
    } catch (err) { toast.error('Failed to delete'); }
    finally { setSaving(false); }
  };

  return (
    <div className="space-y-6" data-testid="templates-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-semibold text-[#0A2342]" style={{ fontFamily: 'Outfit, sans-serif' }}>Marketing Templates</h1>
          <p className="text-[#64748B] mt-1">Email and landing page templates for campaigns</p>
        </div>
        <div className="flex gap-2">
          <Select value={typeFilter} onValueChange={setTypeFilter}>
            <SelectTrigger className="w-40"><SelectValue placeholder="All Types" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Types</SelectItem>
              <SelectItem value="email">Email</SelectItem>
              <SelectItem value="landing_page">Landing Page</SelectItem>
            </SelectContent>
          </Select>
          <Button onClick={openCreate} className="careiq-btn-primary" data-testid="create-template-btn"><Plus className="w-4 h-4 mr-2" />New Template</Button>
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center py-12"><Loader2 className="w-8 h-8 text-[#0E7490] animate-spin" /></div>
      ) : templates.length > 0 ? (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {templates.map((t) => (
            <Card key={t.id} className="careiq-card hover:border-[#0E7490] transition-colors" data-testid={`template-${t.id}`}>
              <CardContent className="p-4 space-y-3">
                <div className="flex items-start justify-between">
                  <div>
                    <h3 className="font-semibold text-[#0A2342] text-sm">{t.name}</h3>
                    <div className="flex items-center gap-2 mt-1">
                      <span className="text-[10px] px-2 py-0.5 rounded-full font-medium flex items-center gap-1" style={{ backgroundColor: t.template_type === 'email' ? '#0E7490' + '15' : '#7C3AED' + '15', color: t.template_type === 'email' ? '#0E7490' : '#7C3AED' }}>
                        {t.template_type === 'email' ? <Mail className="w-3 h-3" /> : <Globe className="w-3 h-3" />}
                        {t.template_type === 'email' ? 'Email' : 'Landing Page'}
                      </span>
                      {t.category && <span className="text-[10px] px-2 py-0.5 bg-[#F1F5F9] text-[#64748B] rounded-full">{t.category}</span>}
                    </div>
                  </div>
                </div>
                {t.subject && <div className="text-xs text-[#64748B]">Subject: {t.subject}</div>}
                {/* Mini preview */}
                {t.html_content && (
                  <div className="h-24 overflow-hidden rounded-lg border border-[#E2E8F0] bg-white p-2 text-[8px]" style={{ lineHeight: '1.2' }}>
                    <div dangerouslySetInnerHTML={{ __html: t.html_content }} style={{ transform: 'scale(0.3)', transformOrigin: 'top left', width: '333%', height: '333%' }} />
                  </div>
                )}
                <div className="flex gap-1">
                  <Button size="sm" variant="outline" className="flex-1" onClick={() => setShowPreview(t)} data-testid={`preview-template-${t.id}`}><Eye className="w-3 h-3 mr-1" />Preview</Button>
                  <Button size="sm" variant="ghost" onClick={() => openEdit(t)}><Edit className="w-3.5 h-3.5" /></Button>
                  <Button size="sm" variant="ghost" onClick={() => setShowDelete(t.id)}><Trash2 className="w-3.5 h-3.5 text-[#991B1B]" /></Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      ) : (
        <Card className="careiq-card"><CardContent className="py-12 text-center text-[#64748B]"><FileText className="w-12 h-12 mx-auto mb-3 text-[#94A3B8]" /><p>No templates yet</p></CardContent></Card>
      )}

      {/* Create/Edit Dialog */}
      <Dialog open={showForm} onOpenChange={setShowForm}>
        <DialogContent className="sm:max-w-2xl max-h-[85vh]" data-testid="template-form-dialog">
          <DialogHeader><DialogTitle>{editId ? 'Edit Template' : 'New Template'}</DialogTitle></DialogHeader>
          <ScrollArea className="max-h-[65vh]">
            <div className="space-y-3 pr-2">
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Template Name *</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="template-name" /></div>
                <div>
                  <Label>Type</Label>
                  <Select value={form.template_type} onValueChange={(v) => setForm({ ...form, template_type: v })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="email">Email</SelectItem>
                      <SelectItem value="landing_page">Landing Page</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Category</Label>
                  <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                    <SelectTrigger><SelectValue placeholder="Select category" /></SelectTrigger>
                    <SelectContent>{CATEGORIES.map(c => <SelectItem key={c} value={c}>{c.charAt(0).toUpperCase() + c.slice(1)}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div><Label>Subject</Label><Input value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} /></div>
              </div>
              <div>
                <Label className="flex items-center gap-1"><Code className="w-3 h-3" />HTML Content</Label>
                <Textarea value={form.html_content} onChange={(e) => setForm({ ...form, html_content: e.target.value })} className="min-h-[200px] font-mono text-xs" placeholder="<div>Your template HTML...</div>" data-testid="template-html" />
              </div>
              {form.html_content && (
                <div>
                  <Label>Preview</Label>
                  <div className="border border-[#E2E8F0] rounded-lg p-4 bg-white min-h-[100px]" dangerouslySetInnerHTML={{ __html: form.html_content }} />
                </div>
              )}
            </div>
          </ScrollArea>
          <div className="flex justify-end gap-3 mt-2">
            <Button variant="outline" onClick={() => setShowForm(false)}>Cancel</Button>
            <Button className="careiq-btn-primary" onClick={handleSave} disabled={saving} data-testid="save-template-btn">{saving ? 'Saving...' : 'Save'}</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Preview Dialog */}
      <Dialog open={!!showPreview} onOpenChange={() => setShowPreview(null)}>
        <DialogContent className="sm:max-w-3xl max-h-[85vh]" data-testid="template-preview-dialog">
          {showPreview && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  {showPreview.template_type === 'email' ? <Mail className="w-5 h-5 text-[#0E7490]" /> : <Globe className="w-5 h-5 text-[#7C3AED]" />}
                  {showPreview.name}
                </DialogTitle>
                {showPreview.subject && <p className="text-sm text-[#64748B]">Subject: {showPreview.subject}</p>}
              </DialogHeader>
              <ScrollArea className="max-h-[65vh]">
                <div className="border border-[#E2E8F0] rounded-lg bg-white p-4" dangerouslySetInnerHTML={{ __html: showPreview.html_content || '<p>No content</p>' }} />
              </ScrollArea>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Delete */}
      <Dialog open={!!showDelete} onOpenChange={() => setShowDelete(null)}>
        <DialogContent className="sm:max-w-sm">
          <DialogHeader><DialogTitle className="text-[#991B1B]">Delete Template</DialogTitle></DialogHeader>
          <p className="text-sm text-[#64748B] mt-2">Delete this template permanently?</p>
          <div className="flex justify-end gap-3 mt-4">
            <Button variant="outline" onClick={() => setShowDelete(null)}>Cancel</Button>
            <Button className="bg-[#991B1B] hover:bg-[#7F1D1D] text-white" onClick={handleDelete} disabled={saving}>Delete</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
